import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { kanbify } from '../lib/kanbifyClient'
import { useAuth } from '../contexts/AuthContext'

/**
 * Heartbeat hook — POSTs to mark_user_active every 30s while the tab is visible.
 * Stops pinging when:
 *   - Tab is hidden (browser background)
 *   - User isn't authenticated
 *
 * Used by the admin cockpit to detect who's actively using the app right now
 * (last_seen_at < 2 min = "online"; > 30 min in work hours = idle alert).
 *
 * Best-effort: silent failures, never block UI.
 */
const HEARTBEAT_INTERVAL_MS = 30_000
const e2eMocks = import.meta.env.VITE_E2E_MOCKS === 'true'

export function useHeartbeat() {
    const { user } = useAuth()
    const location = useLocation()
    const intervalRef = useRef(null)
    const pageRef = useRef(location.pathname)

    useEffect(() => {
        pageRef.current = location.pathname
    }, [location.pathname])

    useEffect(() => {
        if (!user?.id || e2eMocks) return

        const ping = async () => {
            if (document.visibilityState !== 'visible') return
            try {
                await kanbify.rpc('mark_user_active', {
                    p_page: pageRef.current,
                    p_user_agent: navigator.userAgent.slice(0, 500),
                })
            } catch {
                // silent
            }
        }

        // Fire once immediately so admin sees them as soon as they load
        ping()

        // Then every 30s
        intervalRef.current = setInterval(ping, HEARTBEAT_INTERVAL_MS)

        // Re-ping when tab becomes visible after hiding
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') ping()
        }
        document.addEventListener('visibilitychange', handleVisibility)

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
            document.removeEventListener('visibilitychange', handleVisibility)
        }
    }, [user?.id])
}
